import m from "mithril";
import { MithrilTsxComponent } from "mithril-tsx-component";
import { createCanvas } from "oj-canvas-utils";
import { EventAggregator } from "oj-eventaggregator";
import { animateClassNames, animateCreate, animateRemove } from "oj-mithriljs-utils";
import { debounce, delegate, pause, singleton } from "oj-promise-utils";
import { clamp, cleanString, mapRange, memoize, throttle } from "oj-utils";
import { contact_url, getFullProduct } from "../../state/api";
import { renderMap, renderPosMap, rendersProductIndices } from "../../state/renders";
import state from "../../state/state";
import { getText, getTextFixed } from "../../state/translations";
import { getBoundingClientRectFrom } from "../../utils/dom";
import { getAsset } from "../../utils/utils";
import { Button } from "../button/button";
import { dialog } from "../dialog/dialog";
import { Frag } from "../frag/frag";
import { sharedProductUtils } from "../productDetail/productDetail";
import { Svg } from "../svg/svg";
import "./visualization.scss";
export const ea = new EventAggregator();
class ProductButtons extends MithrilTsxComponent {
    subs = [];
    oninit() {
        this.prefetch();
        this.subs.push(state.on("rebuild", () => this.prefetch()));
        this.subs.push(state.on("answers:change", () => this.prefetch()));
    }
    // prime the shared getFullProduct cache so tooltips show storefront data immediately
    prefetch() {
        for (let product of state.products.base) {
            const variant = state.getVariant(product.category);
            if (variant)
                product = { ...product, ...variant };
            getFullProduct(product).catch(err => console.error(err));
        }
    }
    view(v) {
        // const products = state.wissel.products
        return m("div", { className: cleanString("ProductButtons", v.attrs.hidden && "animate-in", animateClassNames(v)) }, state.products.base.map(product => {
            if (!(product.category in renderPosMap[state.wissel.type.value]))
                return m(Frag, { key: product.category });
            const fallback = "fallback" in product ? product.fallback : false;
            const pos = renderPosMap[state.wissel.type.value][product.category];
            const className = `product-${product.category}`;
            const enter = singleton(async (e) => {
                sharedProductUtils.openTooltip(e, product, `.${className}`, (p, e) => {
                    click(e);
                }, fallback, pos.tooltipBottom);
                v.attrs.onhover?.(product);
                const sub = sharedProductUtils.ea.on("tooltip:close", (p) => {
                    if (p === product) {
                        v.attrs.onhover?.(undefined);
                        sub.off();
                    }
                });
            }, () => product.artCodeTSNL);
            const click = singleton(async (e) => {
                if (fallback) {
                    await dialog({
                        className: "confirm-dialog-disabled",
                        title: getText("CONTACT_US"),
                        dismiss: true,
                        closeIcon: true
                    }, c => [
                        m("p", null, getText("PRODUCT_NOT_POSSIBLE_CONTACT_US")),
                        m("div", { className: "dialog-footer" },
                            m(Button, { text: getTextFixed("CONTACT_US"), className: "button-secondary", onclick: () => {
                                    const w = window.open(contact_url, "_blank", "noopener,noreferrer");
                                    c();
                                } }))
                    ]).promise;
                    return;
                }
                v.attrs.onclick?.(product);
                await sharedProductUtils.openDialog(e, product);
            }, () => product.artCodeTSNL);
            return m(Frag, { key: product.category },
                m("button", { className: cleanString(className, fallback && "-fallback"), style: {
                        left: pos.x,
                        top: pos.y,
                    }, onpointerenter: enter, onclick: click },
                    m(Svg, { src: getAsset("icons/plus-white.svg") })),
                pos.rect?.map(rect => m("div", { className: cleanString(className, fallback && "-fallback"), style: {
                        left: rect.x,
                        top: rect.y,
                        width: rect.width,
                        height: rect.height,
                        clipPath: `polygon(${rect.clip})`
                    }, onpointerenter: enter, onclick: click })));
        }));
    }
    oncreate(v) {
        animateCreate(v);
    }
    onbeforeremove(v) {
        return animateRemove(v);
    }
    onremove(v) {
        for (const [k, t] of sharedProductUtils.tooltips)
            t.close();
        for (const [k, d] of sharedProductUtils.dialogs)
            d.close();
        window.off("pointermove.ProductTooltip");
        this.subs.forEach(s => s.off());
    }
}
export class Visualisation extends MithrilTsxComponent {
    image;
    canvas;
    translateProductProgress = 0;
    focusProductProgress = 0;
    translateProductDuration = 1500;
    focusProductDuration = 250;
    translatePathProgress = 0;
    translatePathDuration = 2000;
    ro;
    abortTranslateProduct;
    abortFocusProduct;
    abortPath;
    abortRoot = new AbortController();
    subs = [];
    zoomScale = 2;
    zoomStyle;
    containerEl;
    abortAnimateCanvas;
    resetZoomStyle;
    wissel;
    product;
    focusProductFallback;
    visEl;
    visWidth;
    visHeight;
    oninit(v) {
        this.subs.push(ea.on("transition:begin", () => {
            delete this.wissel;
        }));
        this.subs.push(ea.on("transition:end", () => {
            this.wissel = state.wissel.type.value;
        }));
        let signal;
        const loop = async () => {
            signal?.abort();
            signal = new AbortController();
            const sig = signal.signal;
            if (this.abortRoot.signal.aborted)
                return;
            if (!sharedProductUtils.dialogs.size)
                await this.animatePath();
            while (!sig.aborted && !this.abortRoot.signal.aborted) {
                await pause(8000).promise;
                if (!sig.aborted && !this.abortRoot.signal.aborted && !sharedProductUtils.dialogs.size)
                    await this.animatePath();
            }
        };
        this.subs.push(ea.on("transition:end", loop));
        this.subs.push(ea.on("transition:begin", () => signal?.abort()));
    }
    view(v) {
        return (m("div", { className: "Visualization", style: { "--speed": this.resetZoomStyle ? "0s" : undefined, "--vis-width": this.visWidth + "px", "--vis-height": this.visHeight + "px" }, oncreate: ({ dom }) => {
                this.visEl = dom;
                this.visWidth = this.visEl.clientWidth;
                this.visHeight = this.visEl.clientHeight;
            } },
            m("div", { className: "render-container", style: { transform: this.zoomStyle?.transform }, oncreate: ({ dom }) => this.containerEl = dom },
                m("canvas", { oncreate: ({ dom }) => this.setCanvas(v, dom) }),
                m(Frag, null, state.wissel.type.value === this.wissel && this.translateProductProgress > .5 &&
                    m(ProductButtons, { key: state.wissel.type.value, hidden: !!this.zoomStyle, oncreate: ({ dom }) => {
                            // dom.on("click", async (e) => {
                            //     if (e.target === dom)
                            //         await this.zoomToPos({ x: e.clientX,y: e.clientY }) // todo
                            // })
                        } })))));
    }
    _productOpened = new Set();
    _productFocussed = new Set();
    async onbeforeupdate(v, o) {
        for (const k of this._productOpened)
            if (!sharedProductUtils.dialogs.has(k)) {
                this._productOpened.delete(k);
                delete this.product;
                await Promise.all([
                    this.zoomProductOut(),
                    this.focusProductOut()
                ]);
            }
        for (const [k, d] of sharedProductUtils.dialogs)
            if (!this.product && !this._productOpened.has(k)) {
                this.product = d.product;
                this._productOpened.add(k);
                if (window.innerWidth > 500)
                    await pause(100).promise;
                await this.zoomProductIn(d.product);
                this.focusProductFallback = "fallback" in d.product && d.product.fallback ? true : false;
                await this.focusProductIn(d.product);
            }
        if (sharedProductUtils.dialogs.size > 0)
            return;
        for (const k of this._productFocussed)
            if (!sharedProductUtils.tooltips.has(k)) {
                this._productFocussed.delete(k);
                await this.focusProductOut();
            }
        for (const [k, t] of sharedProductUtils.tooltips)
            if (!this._productFocussed.has(k)) {
                this._productFocussed.add(k);
                this.focusProductFallback = "fallback" in t.product && t.product.fallback ? true : false;
                await this.focusProductIn(t.product);
            }
    }
    onremove() {
        this.abortRoot.abort();
        this.ro?.disconnect();
        this.subs?.forEach(sub => sub.off());
    }
    _focusProductImages;
    async focusProductIn(product) {
        // this.abortPath?.abort()
        this.abortFocusProduct?.abort();
        this.abortFocusProduct = new AbortController();
        const [base, bw, wood, products] = renderMap[state.wissel.type.value];
        const image = [state.wissel.wisselLigger.value === "WOOD" ? wood : base, bw];
        const prod = products[rendersProductIndices.indexOf(product.category)];
        if (prod)
            image.push(prod);
        await this.prefetchCanvas(image.flat(1));
        const images = image.slice(2);
        const d = delegate();
        const startTime = performance.now();
        const abortSignal = this.abortFocusProduct.signal;
        const currProgress = images.join() === this._focusProductImages?.join() ? this.focusProductProgress : 0;
        const loop = () => {
            if (abortSignal?.aborted || this.abortRoot.signal.aborted) {
                d.reject();
                return;
            }
            const elapsed = performance.now() - startTime;
            const progress = Math.max(currProgress, Math.min(elapsed / this.focusProductDuration, 1));
            this._focusProductImages = images;
            this.focusProductProgress = progress;
            this.drawCanvas();
            if (progress >= 1)
                d.resolve();
            else
                requestAnimationFrame(loop);
        };
        loop();
        try {
            await d.promise;
            m.redraw();
        }
        catch {
            // aborted
        }
    }
    async focusProductOut() {
        // this.abortPath?.abort()
        this.abortFocusProduct?.abort();
        this.abortFocusProduct = new AbortController();
        const d = delegate();
        const startTime = performance.now();
        const abortSignal = this.abortFocusProduct.signal;
        const loop = () => {
            if (abortSignal?.aborted || this.abortRoot.signal.aborted) {
                d.reject();
                return;
            }
            const elapsed = performance.now() - startTime;
            const progress = 1 - Math.min(elapsed / this.focusProductDuration, 1);
            this.focusProductProgress = progress;
            this.drawCanvas();
            if (progress <= 0)
                d.resolve();
            else
                requestAnimationFrame(loop);
        };
        loop();
        try {
            await d.promise;
            delete this._focusProductImages;
            this.drawCanvas();
            m.redraw();
        }
        catch {
            // aborted
        }
    }
    async zoomToPos(pos, instant = false, product = this.product) {
        if (this.zoomStyle) {
            return;
            // this.resetZoomStyle = true
            // this.zoomProductOut()
            // m.redraw.sync()
            // await this.zoomToPos(pos, instant, product)
            // m.redraw.sync()
            // this.resetZoomStyle = false
            // return
        }
        const containerRect = getBoundingClientRectFrom(this.containerEl);
        const isMobile = window.innerWidth <= 768;
        const visRect = getBoundingClientRectFrom(this.visEl);
        const viewportCenter = {
            x: window.innerWidth * (isMobile ? .5 : ((window.innerWidth - 500) * .5) / window.innerWidth),
            y: visRect.y + (isMobile ? visRect.height * .20 : visRect.height * .5),
        };
        const scale = isMobile ? this.zoomScale * 2 : this.zoomScale;
        const translateX = viewportCenter.x - containerRect.x - (pos.x - containerRect.x) * scale;
        const translateY = viewportCenter.y - containerRect.y - (pos.y - containerRect.y) * scale;
        this.zoomStyle = {
            x: translateX,
            y: translateY,
            scale: scale,
            transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
        };
        if (product) {
            const [base, bw, wood, products] = renderMap[state.wissel.type.value];
            const images = [state.wissel.wisselLigger.value === "WOOD" ? wood : base, bw];
            const prod = products[rendersProductIndices.indexOf(product.category)];
            if (prod)
                images.push(prod);
            await this.prefetchCanvas(images.flat(1));
            this.drawCanvas();
        }
        m.redraw();
    }
    async zoomProductIn(product, instant = false) {
        if (!this.containerEl)
            return;
        const className = `product-${product.category}`;
        const sourceEvent = (sharedProductUtils.dialogs.get(product.artCodeTSNL) ?? sharedProductUtils.tooltips.get(product.artCodeTSNL))?.event;
        let el = (sourceEvent?.currentTarget ?? sourceEvent?.target)?.closest(`.${className}`) ?? document.querySelector(`.${className}`);
        if (!el)
            return;
        let targetCenter;
        if (el.matches("button")) {
            const divs = Array.from(document.querySelectorAll(`div.${className}`));
            const [first, ...rest] = divs.map(x => ({ el: x, rect: getBoundingClientRectFrom(x) }));
            const overlapping = rest.filter(x => {
                const xr = x.rect;
                return !(xr.x + xr.width < first.rect.x || xr.x > first.rect.x + first.rect.width || xr.y + xr.height < first.rect.y || xr.y > first.rect.y + first.rect.height);
            });
            const bounds = overlapping.reduce((s, x) => ({
                x: Math.min(s.x, x.rect.x),
                y: Math.min(s.y, x.rect.y),
                width: Math.max(s.x + s.width, x.rect.x + x.rect.width) - Math.min(s.x, x.rect.x),
                height: Math.max(s.y + s.height, x.rect.y + x.rect.height) - Math.min(s.y, x.rect.y),
            }), first.rect);
            targetCenter = {
                x: bounds.x + bounds.width * .5,
                y: bounds.y + bounds.height * .5,
            };
        }
        else {
            const r = getBoundingClientRectFrom(el);
            targetCenter = {
                x: r.x + r.width * .5,
                y: (r.y + r.height * .5),
            };
        }
        await this.zoomToPos(targetCenter, instant, product);
    }
    async zoomProductOut() {
        delete this.zoomStyle;
        m.redraw();
    }
    loadImage = memoize(src => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.src = src;
    }));
    prefetched = new Map();
    async prefetchCanvas(image = this.image) {
        const promises = [];
        for (const x of image ?? [])
            if (!this.prefetched.has(x)) {
                const load = this.loadImage(getAsset(`renders/${x}`));
                promises.push(load);
                load.then(img => this.prefetched.set(x, img));
            }
        await Promise.all(promises);
    }
    setCanvas(v, canvas) {
        this.canvas = canvas;
        const [base, bw, wood] = renderMap[state.wissel.type.value];
        this.animateCanvas([state.wissel.wisselLigger.value === "WOOD" ? wood : base, bw]);
        this.ro?.disconnect();
        const resetZoom = debounce(300, async () => {
            if (this.product) {
                this.resetZoomStyle = true;
                this.zoomProductOut();
                m.redraw.sync();
                this.zoomProductIn(this.product, true);
                m.redraw.sync();
                this.resetZoomStyle = false;
            }
        });
        this.ro = new ResizeObserver((e) => {
            this.buffers.clear();
            this.drawCanvas();
            resetZoom();
        });
        this.ro.observe(document.body);
        this.subs.push(state.on("wissel:change", ({ value }) => {
            const [base, bw, wood] = renderMap[value];
            this.animateCanvas([state.wissel.wisselLigger.value === "WOOD" ? wood : base, bw]);
            m.redraw();
        }));
        this.subs.push(state.on("wisselLigger:change", ({ value }) => {
            const [base, bw, wood] = renderMap[state.wissel.type.value];
            this.animateCanvas([value === "WOOD" ? wood : base, bw]);
            m.redraw();
        }));
        this.subs.push(state.on("loaded", () => {
            const [base, bw, wood] = renderMap[state.wissel.type.value];
            this.animateCanvas([state.wissel.wisselLigger.value === "WOOD" ? wood : base, bw]);
            m.redraw();
        }));
    }
    async animateCanvas(image) {
        this.abortAnimateCanvas?.abort();
        const signal = this.abortAnimateCanvas = new AbortController();
        this.abortPath?.abort();
        this.abortTranslateProduct?.abort();
        ea.emit("transition:begin", undefined);
        await this.prefetchCanvas(image);
        if (this.image) {
            const signalOut = this.abortTranslateProduct = new AbortController();
            await this.animateCanvasOut(signalOut.signal);
            m.redraw();
            if (signalOut.signal.aborted || signal.signal.aborted)
                return;
        }
        this.image = image;
        const signalIn = this.abortTranslateProduct = new AbortController();
        await this.animateCanvasIn(signalIn.signal);
        if (signalIn.signal.aborted || signal.signal.aborted)
            return;
        ea.emit("transition:end", undefined);
        m.redraw();
    }
    async animateCanvasOut(abortSignal) {
        await this.prefetchCanvas();
        const d = delegate();
        const startTime = performance.now();
        const loop = () => {
            if ((abortSignal?.aborted || this.abortRoot.signal.aborted)) {
                d.reject();
                return;
            }
            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / this.translateProductDuration, 1) * 3;
            this.translateProductProgress = -progress;
            this.drawCanvas();
            if (progress >= 1)
                d.resolve();
            else
                requestAnimationFrame(loop);
        };
        loop();
        try {
            await d.promise;
            m.redraw();
        }
        catch {
            // aborted
        }
    }
    async animateCanvasIn(abortSignal) {
        await this.prefetchCanvas();
        const d = delegate();
        const startTime = performance.now();
        const loop = () => {
            if ((abortSignal?.aborted || this.abortRoot.signal.aborted)) {
                d.reject();
                return;
            }
            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / this.translateProductDuration, 1);
            this.translateProductProgress = progress;
            this.drawCanvas();
            if (progress >= 1)
                d.resolve();
            else
                requestAnimationFrame(loop);
        };
        loop();
        try {
            await d.promise;
            m.redraw();
        }
        catch {
            // aborted
        }
    }
    _pathImage;
    async animatePath() {
        this.abortPath?.abort();
        const signal = this.abortPath = new AbortController();
        const [base, _, __, ___, path] = renderMap[state.wissel.type.value];
        await this.prefetchCanvas([base, path]);
        const d = delegate();
        const startTime = performance.now();
        const loop = () => {
            if (signal.signal.aborted || this.abortRoot.signal.aborted) {
                d.reject();
                return;
            }
            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / this.translatePathDuration, 1);
            this.translatePathProgress = progress;
            this._pathImage = path;
            this.drawCanvas();
            if (progress >= 1)
                d.resolve();
            else
                requestAnimationFrame(loop);
        };
        loop();
        try {
            await d.promise;
            m.redraw();
        }
        catch {
            // aborted
        }
        delete this._pathImage;
        this.translatePathProgress = 0;
    }
    buffers = new Map();
    drawPath(ctx, progress, pathImg) {
        const width = ctx.canvas.width;
        const height = ctx.canvas.height;
        const shineBuffer = () => {
            let buf = this.buffers.get("path_shine");
            if (!buf) {
                buf = createCanvas({ width, height });
                buf.ctx.clearRect(0, 0, width, height);
                const flash = buf.ctx.createLinearGradient(0, 0, width, 0);
                flash.addColorStop(0, "rgba(255,255,255,0)");
                flash.addColorStop(0.2, "rgba(255,255,255,0)");
                flash.addColorStop(0.4, "rgba(255,255,255,.0)");
                flash.addColorStop(0.5, "rgba(255,255,255,0.9)");
                flash.addColorStop(0.6, "rgba(255,255,255,.0)");
                flash.addColorStop(0.8, "rgba(255,255,255,0)");
                flash.addColorStop(1, "rgba(255,255,255,0)");
                buf.ctx.fillStyle = flash;
                buf.ctx.fillRect(0, 0, width, height);
                this.buffers.set("path_shine", buf);
            }
            return buf;
        };
        const maskBuffer = () => {
            let buf = this.buffers.get("path_mask");
            if (!buf) {
                buf = createCanvas({ width, height });
                buf.canvas.width = width;
                buf.canvas.height = height;
                buf.ctx.clearRect(0, 0, width, height);
                const gradient = buf.ctx.createLinearGradient(0, 0, width, 0);
                gradient.addColorStop(0, "rgba(0, 0, 0, 0)");
                gradient.addColorStop(0.1, "rgba(0, 0, 0, 0)");
                gradient.addColorStop(.3, "rgba(0, 0, 0, 1)");
                gradient.addColorStop(.7, "rgba(0, 0, 0, 1)");
                gradient.addColorStop(0.9, "rgba(0, 0, 0, 0)");
                gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
                buf.ctx.fillStyle = gradient;
                buf.ctx.fillRect(0, 0, width, height);
                this.buffers.set("path_mask", buf);
            }
            return buf;
        };
        const pathBuffer = () => {
            let buf = this.buffers.get(`path_${pathImg.src}`);
            if (!buf) {
                buf = createCanvas({ width, height });
                this.buffers.set(`path_${pathImg.src}`, buf);
            }
            buf.ctx.clearRect(0, 0, width, height);
            buf.ctx.globalCompositeOperation = "source-over";
            const maskBuf = maskBuffer();
            buf.ctx.drawImage(maskBuf.canvas, mapRange(progress, [0, 1], [buf.canvas.width, -buf.canvas.width / 3]), 0, buf.canvas.width / 3, buf.canvas.height, 0, 0, buf.canvas.width, buf.canvas.height);
            buf.ctx.globalCompositeOperation = "source-in";
            buf.ctx.drawImage(pathImg, 0, 0, buf.canvas.width, buf.canvas.height);
            buf.ctx.globalCompositeOperation = "lighter";
            const shineBuf = shineBuffer();
            buf.ctx.drawImage(shineBuf.canvas, mapRange(progress, [0, 1], [buf.canvas.width, -buf.canvas.width / 1.5]), 0, buf.canvas.width / 3, buf.canvas.height, 0, 0, buf.canvas.width, buf.canvas.height);
            buf.ctx.globalCompositeOperation = "destination-in";
            buf.ctx.drawImage(pathImg, 0, 0, buf.canvas.width, buf.canvas.height);
            return buf;
        };
        const pathBuf = pathBuffer();
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
        ctx.drawImage(pathBuf.canvas, 0, 0, ctx.canvas.width, ctx.canvas.height);
    }
    drawBase(ctx, translateProductProgress, baseImg, schematicImg) {
        const drawMaskBuffer = () => {
            const width = ctx.canvas.width;
            const height = ctx.canvas.height;
            let buf = this.buffers.get("mask");
            if (!buf) {
                buf = createCanvas({ width, height });
                const gradient = buf.ctx.createLinearGradient(0, 0, width, 0);
                gradient.addColorStop(0, "rgba(0, 0, 0, 0)");
                gradient.addColorStop(0.3, "rgba(0, 0, 0, 0)");
                gradient.addColorStop(.4, "rgba(0, 0, 0, 1)");
                gradient.addColorStop(.6, "rgba(0, 0, 0, 1)");
                gradient.addColorStop(0.7, "rgba(0, 0, 0, 0)");
                gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
                buf.ctx.fillStyle = gradient;
                buf.ctx.fillRect(0, 0, width, height);
                this.buffers.set("mask", buf);
            }
            return buf;
        };
        const progress = Math.abs(translateProductProgress);
        const directionIn = translateProductProgress > 0;
        const width = ctx.canvas.width;
        const height = ctx.canvas.height;
        let buf = this.buffers.get(`base_${baseImg.src}`);
        if (!buf) {
            buf = createCanvas({ width, height });
            this.buffers.set(`base_${baseImg.src}`, buf);
        }
        else {
            buf.ctx.clearRect(0, 0, width, height);
            buf.ctx.globalCompositeOperation = "source-over";
        }
        if (progress !== 1) {
            const maskBuf = drawMaskBuffer();
            buf.ctx.drawImage(maskBuf.canvas, directionIn
                ? clamp(mapRange(progress, [0, 1], [buf.canvas.width, buf.canvas.width / 2]), buf.canvas.width / 2, buf.canvas.width)
                : clamp(mapRange(progress, [0, 1], [buf.canvas.width / 2, buf.canvas.width / 9]), buf.canvas.width / 9, buf.canvas.width / 2), 0, buf.canvas.width / 9, buf.canvas.height, 0, 0, buf.canvas.width, buf.canvas.height);
            buf.ctx.globalCompositeOperation = "source-in";
        }
        buf.ctx.drawImage(baseImg, 0, 0, buf.canvas.width, buf.canvas.height);
        if (progress !== 1) {
            let buf = this.buffers.get(`base_${schematicImg.src}`);
            if (!buf) {
                buf = createCanvas({ width, height });
                buf.ctx.fillStyle = "#c4dee8";
                buf.ctx.fillRect(0, 0, buf.canvas.width, buf.canvas.height);
                buf.ctx.globalCompositeOperation = "darken";
                buf.ctx.drawImage(schematicImg, 0, 0, buf.canvas.width, buf.canvas.height);
                this.buffers.set(`base_${schematicImg.src}`, buf);
            }
            ctx.globalAlpha = directionIn
                ? progress < .4 ? clamp(mapRange(progress, [0, .4], [0, 1]), 0, 1) : clamp(mapRange(progress, [.4, 1], [1, 0]), 0, 1)
                : 0; //progress < .5 ? clamp(mapRange(progress, [.1, .5], [0, .5]), 0, 1) : clamp(mapRange(progress, [.5, 1], [.5, 0]), 0, 1)
            ctx.drawImage(buf.canvas, 0, 0, ctx.canvas.width, ctx.canvas.height);
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
        ctx.drawImage(buf.canvas, 0, 0, ctx.canvas.width, ctx.canvas.height);
        // const renderSchematic = (progress: number) => {
        //     ctx.globalAlpha = progress
        //     ctx.globalCompositeOperation = "darken"
        //     ctx.drawImage(schematicImg, 0, 0, ctx.canvas.width, ctx.canvas.height)
        //     ctx.globalAlpha = 1.0
        // }
        // const renderBase = (progress: number) => {
        //     ctx.globalAlpha = progress
        //     ctx.globalCompositeOperation = "source-over"
        //     ctx.drawImage(baseImg, 0, 0, ctx.canvas.width, ctx.canvas.height)
        //     ctx.globalAlpha = 1.0
        // }
        // const renderGradient = (progress: number) => {
        //     ctx.globalAlpha = clamp(mapRange(progress, [0, .4], [0, 1]), 0, 1)
        //     const w = mapRange(progress, [.5, 1], [ctx.canvas.width, ctx.canvas.width * 2])
        //     const gradient = ctx.createLinearGradient(0, 0, w, 0)
        //     gradient.addColorStop(clamp(mapRange(progress, [.6, 1], [-.1, 1]), 0, 1), "rgba(0, 0, 0, 0)")
        //     gradient.addColorStop(clamp(mapRange(progress, [.55, 1], [0, 1.2]), 0, 1), "rgba(0, 0, 0, 1)")
        //     gradient.addColorStop(clamp(mapRange(progress, [.5, 1], [.1, 1.4]), 0, 1), "rgba(0, 0, 0, 0)")
        //     ctx.fillStyle = gradient
        //     ctx.globalCompositeOperation = "destination-out"
        //     ctx.fillRect(0, 0, w, ctx.canvas.height)
        //     ctx.globalAlpha = 1.0
        // }
        // const progress = Math.abs(translateProductProgress)
        // if (translateProductProgress > 0) {
        //     renderSchematic(progress < .4 ? clamp(mapRange(progress, [0, .4], [0, 1]), 0, 1) : clamp(mapRange(progress, [.4, 1], [1, 0]), 0, 1))
        //     renderBase(clamp(mapRange(progress, [.5, 1], [0, 1]), 0, 1))
        //     renderGradient(progress)
        // }
        // else if (translateProductProgress < 0) {
        //     renderBase(clamp(mapRange(progress, [0, .8], [1, 0]), 0, 1))
        //     renderSchematic(progress < .5 ? clamp(mapRange(progress, [.1, .5], [0, .5]), 0, 1) : clamp(mapRange(progress, [.5, 1], [.5, 0]), 0, 1))
        // }
    }
    drawFocus(ctx, focusProductProgress, overlayImgs, fallback = false) {
        const width = ctx.canvas.width;
        const height = ctx.canvas.height;
        if (fallback) {
            let buf = this.buffers.get(`focus_${overlayImgs.map(x => x.src).join()}_${fallback ? 1 : 0}`);
            if (!buf) {
                buf = createCanvas({ width, height });
                for (const img of overlayImgs)
                    buf.ctx.drawImage(img, 0, 0, width, height);
                buf.ctx.globalCompositeOperation = "source-in";
                buf.ctx.globalAlpha = 0.5;
                buf.ctx.fillStyle = "#ff7a22";
                buf.ctx.fillRect(0, 0, width, height);
                this.buffers.set(`focus_${overlayImgs.map(x => x.src).join()}_${fallback ? 1 : 0}`, buf);
            }
            const progress = Math.abs(focusProductProgress);
            ctx.globalAlpha = progress;
            ctx.globalCompositeOperation = "source-over";
            ctx.drawImage(buf.canvas, 0, 0, width, height);
            ctx.globalAlpha = 1.0;
        }
        else {
            const progress = Math.abs(focusProductProgress);
            ctx.globalAlpha = progress;
            ctx.globalCompositeOperation = "source-over";
            for (const img of overlayImgs)
                ctx.drawImage(img, 0, 0, width, height);
            ctx.globalAlpha = 1.0;
        }
    }
    drawCanvas = throttle(16, () => {
        if (!this.canvas || !this.image || this.translateProductProgress === 0)
            return;
        const baseImg = this.prefetched.get(this.image[0]);
        if (!baseImg)
            return;
        const schematicImg = this.prefetched.get(this.image[1]);
        const overlayImgs = this._focusProductImages?.map(x => this.prefetched.get(x)) ?? [];
        const pathImg = this._pathImage ? this.prefetched.get(this._pathImage) : undefined;
        const zoom = this.zoomStyle?.scale ?? 1;
        this.visWidth = this.visEl.clientWidth;
        this.visHeight = this.visEl.clientHeight;
        const scale = Math.min(this.visWidth * zoom / baseImg.width, this.visHeight * zoom / baseImg.height);
        const w = baseImg.width * scale;
        const h = baseImg.height * scale;
        if (w !== this.canvas.width || h !== this.canvas.height)
            this.buffers.clear();
        this.canvas.width = w;
        this.canvas.height = h;
        const ctx = this.canvas.getContext("2d");
        ctx.fillStyle = "#c4dee8";
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.drawBase(ctx, this.translateProductProgress, baseImg, schematicImg);
        if (overlayImgs.length)
            this.drawFocus(ctx, this.focusProductProgress, overlayImgs, this.focusProductFallback);
        if (pathImg)
            this.drawPath(ctx, this.translatePathProgress, pathImg);
    }, true);
}
